import React, {
  forwardRef,
  useCallback,
  useId,
  useImperativeHandle,
  useState,
} from "react";
import { Button, Input } from "antd";
import { getLanguageName } from "../../utils/languageUtils";
import "./EpisodeDrawer.scss";

interface EpisodeInfo {
  name: string;
  sourceLanguage: string;
  sourceLangName?: string;
  targetLanguage: string;
  needRemovalSubtitle: boolean;
}

interface Speaker {
  id: number;
  speaker_name: string;
  description?: string;
}

interface CharacterProfile {
  name: string;
  description: string;
}

interface EpisodeDrawerProps {
  episodeInfo: EpisodeInfo;
  speakersData: Speaker[];
  speakersLoading: boolean;
  seriesName?: string | null;
  characterProfiles?: CharacterProfile[]; // 角色信息数组（优先显示）
  /** 为 false 时不渲染默认圆形抽屉按钮，可通过 ref.toggleDrawer() 触发 */
  showToggleButton?: boolean;
}

export type EpisodeDrawerHandle = {
  toggleDrawer: () => void;
};

export const EpisodeDrawer = forwardRef<
  EpisodeDrawerHandle,
  EpisodeDrawerProps
>(function EpisodeDrawer(
  {
    episodeInfo,
    speakersData,
    speakersLoading,
    seriesName,
    characterProfiles,
    showToggleButton = true,
  },
  ref
) {
  const drawerTitleId = useId();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerAnimating, setDrawerAnimating] = useState(false);
  const [drawerOpening, setDrawerOpening] = useState(false);

  // 切换抽屉显示状态
  const toggleDrawer = useCallback(() => {
    if (drawerVisible) {
      setDrawerAnimating(true);
      const drawerElement = document.querySelector(".episode-drawer");
      const overlayElement = document.querySelector(".drawer-overlay");

      if (drawerElement && overlayElement) {
        drawerElement.classList.add("slide-out");
        overlayElement.classList.add("fade-out");

        setTimeout(() => {
          setDrawerVisible(false);
          setDrawerAnimating(false);
          setDrawerOpening(false);
          drawerElement.classList.remove("slide-out");
          overlayElement.classList.remove("fade-out");
        }, 400);
      } else {
        setDrawerVisible(false);
        setDrawerAnimating(false);
        setDrawerOpening(false);
      }
    } else {
      setDrawerVisible(true);
      setDrawerAnimating(false);
      // 使用 requestAnimationFrame 确保在下一帧添加 visible 类，触发 transition
      requestAnimationFrame(() => {
        setDrawerOpening(true);
      });
    }
  }, [drawerVisible]);

  useImperativeHandle(
    ref,
    () => ({
      toggleDrawer,
    }),
    [toggleDrawer]
  );

  return (
    <>
      {showToggleButton ? (
        <button
          type="button"
          className="drawer-toggle-btn"
          onClick={toggleDrawer}
          aria-label="打开剧集信息"
        >
          <i className="iconfont icon-shouqicebianlan" aria-hidden />
        </button>
      ) : null}

      {/* 剧集信息抽屉 */}
      {drawerVisible && (
        <div
          className={`drawer-overlay ${drawerAnimating ? "fade-out" : ""}`}
          role="presentation"
          onClick={toggleDrawer}
        >
          <div
            className={`episode-drawer ${drawerOpening ? "visible" : ""} ${drawerAnimating ? "slide-out" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={drawerTitleId}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-header">
              <h3 id={drawerTitleId}>剧集信息</h3>
              <Button
                type="text"
                className="drawer-close-btn"
                onClick={toggleDrawer}
                icon={<i className="fas fa-times"></i>}
              />
            </div>
            <div className="drawer-content">
              <div className="form-item">
                <label>剧集名称</label>
                <Input
                  value={seriesName || episodeInfo.name || ''}
                  disabled
                  className="disabled-input"
                  title={String(seriesName || episodeInfo.name || "").trim() || undefined}
                />
              </div>
              <div className="form-item">
                <label>源语言</label>
                <Input
                  value={episodeInfo.sourceLangName || getLanguageName(episodeInfo.sourceLanguage) || episodeInfo.sourceLanguage}
                  disabled
                  className="disabled-input"
                  title={
                    episodeInfo.sourceLangName ||
                    getLanguageName(episodeInfo.sourceLanguage) ||
                    episodeInfo.sourceLanguage ||
                    undefined
                  }
                />
              </div>
              <div className="form-item">
                <label>字幕擦除</label>
                <div className="subtitle-toggle-wrapper">
                  {(() => {
                    
                    return (
                      <div
                        className={`subtitle-toggle ${episodeInfo.needRemovalSubtitle ? 'active' : ''}`}
                        style={{ cursor: 'not-allowed', opacity: 0.7 }}
                      >
                        <div className="toggle-handle"></div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="form-item vertical-layout">
                <label>主要角色</label>
                {speakersLoading ? (
                  <div className="synopsis-role">加载中...</div>
                ) : characterProfiles && characterProfiles.length > 0 ? (
                  characterProfiles.map((character, index) => {
                    const line = `${character.name}：${character.description || ""}`;
                    return (
                      <div key={character.name || index} className="synopsis-role" title={line}>
                        {character.name}：{character.description || ""}
                      </div>
                    );
                  })
                ) : speakersData.length > 0 ? (
                  speakersData.map((speaker, index) => {
                    const line = `${speaker.speaker_name}：${speaker.description || ""}`;
                    return (
                      <div key={speaker.id || index} className="synopsis-role" title={line}>
                        {speaker.speaker_name}：{speaker.description || ""}
                      </div>
                    );
                  })
                ) : (
                  <div className="synopsis-role">暂无角色信息</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

